import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "sa-box",
  role: "dialog",
  "aria-label": "Sessões dos agentes"
}
const _hoisted_2 = { class: "sa-head" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = { class: "sa-search-row" }
const _hoisted_5 = {
  class: "sa-filters",
  role: "group",
  "aria-label": "Filtro"
}
const _hoisted_6 = { class: "sa-selectbar" }
const _hoisted_7 = { class: "sa-check" }
const _hoisted_8 = ["checked", ".indeterminate", "disabled"]
const _hoisted_9 = { class: "sa-count" }
const _hoisted_10 = { class: "sa-list" }
const _hoisted_11 = {
  key: 0,
  class: "sa-state"
}
const _hoisted_12 = {
  key: 1,
  class: "sa-state"
}
const _hoisted_13 = ["title"]
const _hoisted_14 = ["checked", "disabled", "onChange"]
const _hoisted_15 = { class: "sa-avatar" }
const _hoisted_16 = ["src", "onError"]
const _hoisted_17 = { class: "sa-agent-body" }
const _hoisted_18 = { class: "sa-agent-name" }
const _hoisted_19 = {
  key: 0,
  class: "sa-tag sa-tag--warn"
}
const _hoisted_20 = { class: "sa-agent-sub" }
const _hoisted_21 = { class: "sa-sessions" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = { key: 1 }
const _hoisted_24 = {
  key: 0,
  class: "sa-error"
}
const _hoisted_25 = {
  key: 1,
  class: "sa-done"
}
const _hoisted_26 = { class: "sa-foot" }
const _hoisted_27 = {
  class: "sa-scope",
  role: "radiogroup",
  "aria-label": "O que revogar"
}
const _hoisted_28 = ["aria-checked", "onClick"]
const _hoisted_29 = { class: "sa-scope-hint" }
const _hoisted_30 = {
  key: 0,
  class: "sa-actions"
}
const _hoisted_31 = { class: "sa-impact" }
const _hoisted_32 = ["disabled"]
const _hoisted_33 = {
  key: 1,
  class: "sa-confirm"
}
const _hoisted_34 = { class: "sa-confirm-btns" }
const _hoisted_35 = ["disabled"]
const _hoisted_36 = ["disabled"]

import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { toMediaUrl } from '@/utils/media'
import { useStore } from 'vuex'
import { listAgentSessions, revokeAgentSessions, type AgentSessions, type AgentSessionItem, type SessionRevokeScope } from '@/services/wootdashSessions'
import { sessionAdminOpen } from '@/composables/useSessionAdmin'

type Presence = 'online' | 'away' | 'offline'

export default /*@__PURE__*/_defineComponent({
  __name: 'SessionAdminModal',
  setup(__props) {

// Sessões dos agentes (Ctrl+Shift+S, permissão sessions.manage — perfil Sistema). Seleciona vários agentes e revoga a
// sessão atual (a de sinal mais recente), as demais abertas ou as duas. O backend avisa cada sessão pelo hub
// ("SessaoRevogada") e a aba do agente sai com o aviso (useSessionTracking.ts). A sua própria sessão nunca é revogada.
const PRESENCE_LABEL: Record<Presence, string> = { online: 'Online', away: 'Ausente', offline: 'Offline' }
const SCOPES: { value: SessionRevokeScope; label: string; hint: string }[] = [
  { value: 0, label: 'Sessão atual', hint: 'Só a sessão em uso de cada agente (a de sinal mais recente). As outras abas e navegadores continuam.' },
  { value: 1, label: 'Sessões abertas', hint: 'As outras sessões de cada agente (abas e navegadores antigos). A sessão em uso continua.' },
  { value: 2, label: 'As duas', hint: 'Todas as sessões de cada agente. Ele sai do sistema e fica offline.' },
]

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')

const agents = ref<AgentSessions[]>([])
const loading = ref(false)
const busy = ref(false)
const error = ref('')
const done = ref('')
const query = ref('')
const onlyOnline = ref(false)
const scope = ref<SessionRevokeScope>(0)
const confirming = ref(false)
const selected = ref(new Set<string>())
const searchRef = ref<HTMLInputElement | null>(null)
const nowTick = ref(Date.now())
let tickTimer: ReturnType<typeof setInterval> | undefined

const presence = (a: AgentSessions): Presence => {
  const s = a.status.toLowerCase()
  return s === 'online' ? 'online' : s === 'offline' ? 'offline' : 'away'
}
// avatarUrl vem cru da API (caminho relativo de POST /anexos) — toMediaUrl monta a URL exibível, como no resto do
// sistema. Foto que não carrega (arquivo removido) cai pras iniciais.
const brokenAvatars = reactive(new Set<string>())
const avatarSrc = (a: AgentSessions): string | null => (brokenAvatars.has(a.agentId) ? null : toMediaUrl(a.avatarUrl))
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?'
const onlyMine = (a: AgentSessions) => a.sessions.every(s => s.isMine)

const onlineCount = computed(() => agents.value.filter(a => presence(a) === 'online').length)
const visible = computed(() => {
  const q = query.value.trim().toLowerCase()
  return agents.value
    .filter(a => !onlyOnline.value || presence(a) === 'online')
    .filter(a => !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
})
const selectable = computed(() => visible.value.filter(a => !onlyMine(a)))
const allVisibleSelected = computed(() => selectable.value.length > 0 && selectable.value.every(a => selected.value.has(a.agentId)))
const someVisibleSelected = computed(() => selectable.value.some(a => selected.value.has(a.agentId)))

/** Mesma regra do backend: atual = a 1ª (sinal mais recente); a sua nunca entra. */
function willRevoke(a: AgentSessions, s: AgentSessionItem): boolean {
  if (!selected.value.has(a.agentId) || s.isMine) return false
  return scope.value === 2 || (scope.value === 0 ? s.isCurrent : !s.isCurrent)
}
const impact = computed(() => {
  let sessions = 0
  let agentsHit = 0
  for (const a of agents.value) {
    const n = a.sessions.filter(s => willRevoke(a, s)).length
    sessions += n
    if (n) agentsHit++
  }
  return { sessions, agents: agentsHit }
})
const impactLabel = computed(() => {
  if (!selected.value.size) return 'Selecione os agentes na lista.'
  if (!impact.value.sessions) return 'Nenhuma sessão para revogar com essa opção.'
  return `${impact.value.sessions} ${impact.value.sessions === 1 ? 'sessão' : 'sessões'} de ${impact.value.agents} ${impact.value.agents === 1 ? 'agente' : 'agentes'}`
})

function ago(iso: string): string {
  const s = Math.max(0, Math.round((nowTick.value - new Date(iso).getTime()) / 1000))
  if (s < 60) return 'agora'
  const m = Math.round(s / 60)
  if (m < 60) return `há ${m} min`
  const h = Math.round(m / 60)
  return h < 24 ? `há ${h} h` : `há ${Math.round(h / 24)} d`
}

/** User-agent vira "Chrome · Windows"; rótulo curto passa como está. */
function shortDevice(device: string): string {
  if (!device) return 'Desconhecido'
  if (!/mozilla|applewebkit|gecko/i.test(device)) return device
  const browser = /edg\//i.test(device) ? 'Edge' : /opr\//i.test(device) ? 'Opera' : /firefox\//i.test(device) ? 'Firefox'
    : /chrome\//i.test(device) ? 'Chrome' : /safari\//i.test(device) ? 'Safari' : 'Navegador'
  const os = /windows/i.test(device) ? 'Windows' : /android/i.test(device) ? 'Android' : /iphone|ipad/i.test(device) ? 'iOS'
    : /mac os/i.test(device) ? 'macOS' : /linux/i.test(device) ? 'Linux' : ''
  return os ? `${browser} · ${os}` : browser
}

function toggle(id: string) {
  const next = new Set(selected.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selected.value = next
  confirming.value = false
}
function toggleAllVisible() {
  const next = new Set(selected.value)
  if (allVisibleSelected.value) selectable.value.forEach(a => next.delete(a.agentId))
  else selectable.value.forEach(a => next.add(a.agentId))
  selected.value = next
  confirming.value = false
}
watch(scope, () => { confirming.value = false })

async function load() {
  loading.value = true
  error.value = ''
  try {
    agents.value = await listAgentSessions(token.value)
    // Quem saiu da lista (sem sessão) sai da seleção.
    const ids = new Set(agents.value.map(a => a.agentId))
    selected.value = new Set([...selected.value].filter(id => ids.has(id)))
    nowTick.value = Date.now()
  } catch (e: any) {
    error.value = e?.message || 'Não deu pra carregar as sessões.'
  } finally {
    loading.value = false
  }
}

async function revoke() {
  if (busy.value) return
  busy.value = true
  error.value = ''
  done.value = ''
  try {
    const ids = [...selected.value].filter(id => agents.value.some(a => a.agentId === id && a.sessions.some(s => willRevoke(a, s))))
    const result = await revokeAgentSessions(token.value, ids, scope.value)
    done.value = result.revoked
      ? `${result.revoked} ${result.revoked === 1 ? 'sessão revogada' : 'sessões revogadas'} de ${result.agents.length} ${result.agents.length === 1 ? 'agente' : 'agentes'}.`
      : 'Nenhuma sessão foi revogada (as sessões já tinham sido encerradas).'
    selected.value = new Set()
    confirming.value = false
    await load()
  } catch (e: any) {
    error.value = e?.message || 'Não deu pra revogar as sessões.'
  } finally {
    busy.value = false
  }
}

function close() {
  sessionAdminOpen.value = false
}

watch(sessionAdminOpen, async open => {
  if (tickTimer) { clearInterval(tickTimer); tickTimer = undefined }
  if (!open) return
  query.value = ''
  error.value = ''
  done.value = ''
  confirming.value = false
  selected.value = new Set()
  tickTimer = setInterval(() => { nowTick.value = Date.now() }, 30_000)
  await nextTick()
  searchRef.value?.focus()
  await load()
})

function onKeydown(e: KeyboardEvent) {
  if (sessionAdminOpen.value && e.key === 'Escape') {
    if (confirming.value) confirming.value = false
    else close()
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  if (tickTimer) clearInterval(tickTimer)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "sa-fade" }, {
      default: _withCtx(() => [
        (_unref(sessionAdminOpen))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "sa-overlay",
              onMousedown: _withModifiers(close, ["self"])
            }, [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _cache[7] || (_cache[7] = _createElementVNode("span", { class: "sa-head-icon" }, [
                    _createElementVNode("svg", {
                      width: "16",
                      height: "16",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round"
                    }, [
                      _createElementVNode("path", { d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" }),
                      _createElementVNode("polyline", { points: "16 17 21 12 16 7" }),
                      _createElementVNode("line", {
                        x1: "21",
                        y1: "12",
                        x2: "9",
                        y2: "12"
                      })
                    ])
                  ], -1)),
                  _cache[8] || (_cache[8] = _createElementVNode("div", { class: "sa-head-text" }, [
                    _createElementVNode("strong", null, "Sessões dos agentes"),
                    _createElementVNode("span", null, "Revogue sessões e desconecte agentes na hora")
                  ], -1)),
                  _cache[9] || (_cache[9] = _createElementVNode("kbd", { class: "sa-kbd" }, "Ctrl Shift S", -1)),
                  _createElementVNode("button", {
                    type: "button",
                    class: "sa-icon-btn",
                    title: "Atualizar a lista",
                    disabled: loading.value,
                    onClick: load
                  }, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("svg", {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2.4",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round"
                    }, [
                      _createElementVNode("polyline", { points: "23 4 23 10 17 10" }),
                      _createElementVNode("path", { d: "M20.49 15a9 9 0 1 1-2.12-9.36L23 10" })
                    ], -1)
                  ]))], 8, _hoisted_3),
                  _createElementVNode("button", {
                    type: "button",
                    class: "sa-icon-btn",
                    title: "Fechar (Esc)",
                    onClick: close
                  }, [...(_cache[6] || (_cache[6] = [
                    _createElementVNode("svg", {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2.5",
                      "stroke-linecap": "round"
                    }, [
                      _createElementVNode("line", {
                        x1: "18",
                        y1: "6",
                        x2: "6",
                        y2: "18"
                      }),
                      _createElementVNode("line", {
                        x1: "6",
                        y1: "6",
                        x2: "18",
                        y2: "18"
                      })
                    ], -1)
                  ]))])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _cache[12] || (_cache[12] = _createElementVNode("svg", {
                    width: "15",
                    height: "15",
                    viewBox: "0 0 24 24",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2.5",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round"
                  }, [
                    _createElementVNode("circle", {
                      cx: "11",
                      cy: "11",
                      r: "8"
                    }),
                    _createElementVNode("line", {
                      x1: "21",
                      y1: "21",
                      x2: "16.65",
                      y2: "16.65"
                    })
                  ], -1)),
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "searchRef",
                    ref: searchRef,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
                    class: "sa-search",
                    placeholder: "Buscar agente pelo nome ou e-mail...",
                    autocomplete: "off",
                    spellcheck: "false"
                  }, null, 512), [
                    [_vModelText, query.value]
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass({ 'is-on': !onlyOnline.value }),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (onlyOnline.value = false))
                    }, [
                      _cache[10] || (_cache[10] = _createTextVNode("Todos ", -1)),
                      _createElementVNode("b", null, _toDisplayString(agents.value.length), 1)
                    ], 2),
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass({ 'is-on': onlyOnline.value }),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (onlyOnline.value = true))
                    }, [
                      _cache[11] || (_cache[11] = _createTextVNode("Online ", -1)),
                      _createElementVNode("b", null, _toDisplayString(onlineCount.value), 1)
                    ], 2)
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("label", _hoisted_7, [
                    _createElementVNode("input", {
                      type: "checkbox",
                      checked: allVisibleSelected.value,
                      ".indeterminate": someVisibleSelected.value && !allVisibleSelected.value,
                      disabled: !selectable.value.length,
                      onChange: toggleAllVisible
                    }, null, 40, _hoisted_8),
                    _createElementVNode("span", null, "Selecionar " + _toDisplayString(query.value || onlyOnline.value ? 'os da lista' : 'todos'), 1)
                  ]),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(selected.value.size) + " selecionado(s)", 1)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  (loading.value && !agents.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [...(_cache[13] || (_cache[13] = [
                        _createElementVNode("span", { class: "sa-spinner" }, null, -1),
                        _createTextVNode(" Carregando sessões...", -1)
                      ]))]))
                    : (!visible.value.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(agents.value.length ? 'Nenhum agente nesse filtro.' : 'Nenhum agente com sessão aberta.'), 1))
                      : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(visible.value, (a) => {
                          return (_openBlock(), _createElementBlock("label", {
                            key: a.agentId,
                            class: _normalizeClass(["sa-agent", { 'sa-agent--on': selected.value.has(a.agentId), 'sa-agent--locked': onlyMine(a) }]),
                            title: onlyMine(a) ? 'Esta é a sua sessão em uso — ela não pode ser revogada por aqui' : ''
                          }, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              checked: selected.value.has(a.agentId),
                              disabled: onlyMine(a),
                              onChange: ($event: any) => (toggle(a.agentId))
                            }, null, 40, _hoisted_14),
                            _createElementVNode("span", _hoisted_15, [
                              (avatarSrc(a))
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    src: avatarSrc(a)!,
                                    alt: "",
                                    onError: ($event: any) => (brokenAvatars.add(a.agentId))
                                  }, null, 40, _hoisted_16))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createTextVNode(_toDisplayString(initials(a.name)), 1)
                                  ], 64)),
                              _createElementVNode("span", {
                                class: _normalizeClass(["sa-dot", 'sa-dot--' + presence(a)])
                              }, null, 2)
                            ]),
                            _createElementVNode("span", _hoisted_17, [
                              _createElementVNode("span", _hoisted_18, [
                                _createTextVNode(_toDisplayString(a.name) + " ", 1),
                                (!a.isActive)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_19, "desativado"))
                                  : _createCommentVNode("", true)
                              ]),
                              _createElementVNode("span", _hoisted_20, _toDisplayString(a.email), 1),
                              _createElementVNode("span", _hoisted_21, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(a.sessions, (s) => {
                                  return (_openBlock(), _createElementBlock("span", {
                                    key: s.id,
                                    class: _normalizeClass(["sa-session", { 'sa-session--current': s.isCurrent, 'sa-session--hit': willRevoke(a, s) }])
                                  }, [
                                    _createTextVNode(_toDisplayString(shortDevice(s.device)) + " · " + _toDisplayString(ago(s.lastSeenAt)) + " ", 1),
                                    (s.isMine)
                                      ? (_openBlock(), _createElementBlock("em", _hoisted_22, "sua"))
                                      : (s.isCurrent)
                                        ? (_openBlock(), _createElementBlock("em", _hoisted_23, "atual"))
                                        : _createCommentVNode("", true)
                                  ], 2))
                                }), 128))
                              ])
                            ]),
                            _createElementVNode("span", {
                              class: _normalizeClass(["sa-status", 'sa-status--' + presence(a)])
                            }, _toDisplayString(PRESENCE_LABEL[presence(a)]), 3)
                          ], 10, _hoisted_13))
                        }), 128))
                ]),
                (error.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_24, _toDisplayString(error.value), 1))
                  : _createCommentVNode("", true),
                (done.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(done.value), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_26, [
                  _createElementVNode("div", _hoisted_27, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(SCOPES, (opt) => {
                      return _createElementVNode("button", {
                        key: opt.value,
                        type: "button",
                        role: "radio",
                        "aria-checked": scope.value === opt.value,
                        class: _normalizeClass({ 'is-on': scope.value === opt.value }),
                        onClick: ($event: any) => (scope.value = opt.value)
                      }, _toDisplayString(opt.label), 11, _hoisted_28)
                    }), 64))
                  ]),
                  _createElementVNode("p", _hoisted_29, _toDisplayString(SCOPES[scope.value].hint), 1),
                  (!confirming.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                        _createElementVNode("span", _hoisted_31, _toDisplayString(impactLabel.value), 1),
                        _createElementVNode("button", {
                          type: "button",
                          class: "sa-btn sa-btn--danger",
                          disabled: !impact.value.sessions || busy.value,
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (confirming.value = true))
                        }, "Revogar", 8, _hoisted_32)
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_33, [
                        _createElementVNode("span", null, [
                          _cache[14] || (_cache[14] = _createTextVNode(" Revogar ", -1)),
                          _createElementVNode("b", null, _toDisplayString(impact.value.sessions) + " " + _toDisplayString(impact.value.sessions === 1 ? 'sessão' : 'sessões'), 1),
                          _cache[15] || (_cache[15] = _createTextVNode(" de ", -1)),
                          _createElementVNode("b", null, _toDisplayString(impact.value.agents) + " " + _toDisplayString(impact.value.agents === 1 ? 'agente' : 'agentes'), 1),
                          _cache[16] || (_cache[16] = _createTextVNode("? Eles veem \"Esta sessão foi revogada pelo Sistema\" e são desconectados. ", -1))
                        ]),
                        _createElementVNode("div", _hoisted_34, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "sa-btn",
                            disabled: busy.value,
                            onClick: _cache[4] || (_cache[4] = ($event: any) => (confirming.value = false))
                          }, "Cancelar", 8, _hoisted_35),
                          _createElementVNode("button", {
                            type: "button",
                            class: "sa-btn sa-btn--danger",
                            disabled: busy.value,
                            onClick: revoke
                          }, _toDisplayString(busy.value ? 'Revogando...' : 'Sim, revogar'), 9, _hoisted_36)
                        ])
                      ]))
                ])
              ])
            ], 32))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})